import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Gift, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { submitLead, formatApiError } from "@/lib/api";

const REF_KEY = "zf_referral_code";
const SEEN_KEY = "zf_referral_popup_seen";
export const STATEMENT_KEY = "mb_statement_popup_seen";
export const openReferral = () => window.dispatchEvent(new Event("open-referral"));

export const BONUS_TERMS = "Bonus available to qualifying new merchant accounts approved and activated through 866zerofees.com Eligibility and bonus amount are subject to account review and may be based on factors including processing volume, transaction activity, account type, processing history, risk profile, and overall account value. Merchant must meet applicable underwriting requirements and maintain an active processing account in good standing. Bonus is not guaranteed and is subject to final approval by 866zerofees.com Additional terms and conditions may apply. Offer may be modified or discontinued at any time. One bonus per qualifying business.";

const FIELDS = [
  ["name", "Your name", "text", true],
  ["business", "Business name", "text", true],
  ["email", "Email", "email", true],
  ["phone", "Phone", "tel", true],
  ["referred_by", "Who referred you?", "text", true],
];

export const ReferralPopup = () => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", referred_by: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || params.get("referral");
    if (ref) sessionStorage.setItem(REF_KEY, ref.slice(0, 120));
    const saved = sessionStorage.getItem(REF_KEY);
    if (saved) setForm((f) => ({ ...f, referred_by: f.referred_by || saved }));
    if (ref && !sessionStorage.getItem(SEEN_KEY)) {
      sessionStorage.setItem(SEEN_KEY, "1");
      sessionStorage.setItem(STATEMENT_KEY, "1");
      setShow(true);
    }
    const open = () => { sessionStorage.setItem(STATEMENT_KEY, "1"); setShow(true); };
    window.addEventListener("open-referral", open);
    return () => window.removeEventListener("open-referral", open);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please confirm you've read the bonus terms."); return; }
    setBusy(true);
    try {
      await submitLead({ ...form, source: "referral" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="referral-popup"
          className="fixed inset-0 z-[70] flex items-end justify-center bg-[#050B1F]/70 p-3 backdrop-blur-sm sm:items-center sm:p-6" onClick={() => setShow(false)}>
          <motion.div onClick={(e) => e.stopPropagation()} data-lenis-prevent
            initial={{ y: 60, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 170, damping: 20 }}
            className="relative grid max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl md:grid-cols-5">
            <button onClick={() => setShow(false)} data-testid="referral-popup-close" aria-label="Close" className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-[#050B1F] hover:bg-lime-300"><X className="h-4 w-4" /></button>

            <div className="relative overflow-hidden bg-[#050B1F] p-7 text-white md:col-span-2 sm:p-8">
              <div className="grid-bg absolute inset-0" />
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-lime-400/30 blur-3xl" />
              <div className="spin-slow absolute -bottom-24 -left-24 h-64 w-64 rounded-full border-[18px] border-dashed border-lime-300/15" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full bg-lime-400 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#050B1F]"><Gift className="h-3.5 w-3.5" /> Referral bonus</span>
                <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}
                  className="mt-6 font-display text-7xl font-extrabold leading-none tracking-tighter text-lime-300 sm:text-8xl" data-testid="referral-amount">$250</motion.p>
                <p className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight">Qualifying bonus</p>
                <p className="mt-4 text-sm text-blue-100/90">Were you referred to 866Zerofees.com? Get approved and activated as a new merchant and you could receive a $250 bonus.</p>
              </div>
            </div>

            <div className="p-7 md:col-span-3 sm:p-8">
              {done ? (
                <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="referral-popup-success">
                  <div className="grid h-14 w-14 place-items-center rounded-full bg-lime-400"><Check className="h-7 w-7" strokeWidth={3} /></div>
                  <p className="mt-4 font-display text-2xl font-bold text-[#050B1F]">You're registered!</p>
                  <p className="mt-1 max-w-sm text-sm text-slate-700">Your account manager will reach out to get you set up. Bonus eligibility is confirmed after approval and activation.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <p className="font-display text-xl font-bold leading-tight text-[#050B1F]">Claim your referral bonus</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {FIELDS.map(([k, p, t, r]) => (
                      <input key={k} data-testid={`referral-${k}-input`} type={t} required={r} placeholder={p} value={form[k]} onChange={set(k)}
                        className={`w-full rounded-xl border border-[#0B2A6F]/15 px-4 py-3 text-[15px] outline-none focus:border-emerald-600 focus:ring-4 focus:ring-lime-300/40 ${k === "referred_by" ? "sm:col-span-2" : ""}`} />
                    ))}
                  </div>
                  <div className="max-h-28 overflow-y-auto rounded-xl bg-[#EAF3FF] p-3 text-[12px] leading-relaxed text-slate-700" data-testid="referral-terms">
                    <p className="mb-1 font-bold uppercase tracking-wide text-[#0B2A6F]">$250 Qualifying Bonus</p>
                    {BONUS_TERMS}
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 text-[13px] text-slate-700">
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="referral-terms-checkbox" className="mt-0.5 h-4 w-4 accent-[#0B2A6F]" />
                    I've read the bonus terms and understand the bonus is not guaranteed.
                  </label>
                  <button disabled={busy} data-testid="referral-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2A6F] py-3.5 text-sm font-bold text-white hover:bg-[#050B1F] disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Claim my $250 bonus
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
